import type { UseQueryResult } from "@tanstack/react-query";
import { CircleAlertIcon, CloudOffIcon, RefreshCwIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/misc";
import { errorCode, errorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

/** Core not answering: shown in place of the figures while the query keeps trying by itself. */
export function CoreUnavailable() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-xl border border-partial/40 bg-partial-bg p-4 text-partial-fg"
    >
      <CloudOffIcon className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="font-semibold">{t("adminApp.coreUnavailable.title")}</p>
        <p className="mt-0.5 flex items-center gap-1.5 text-sm">
          <RefreshCwIcon className="size-3.5 animate-spin [animation-duration:2.5s]" aria-hidden />
          {t("adminApp.coreUnavailable.body")}
        </p>
      </div>
    </div>
  );
}

/**
 * A query's data, or what stands in for it: a placeholder while it first loads, the "core can't
 * be reached" state while it retries, an error otherwise. A refresh keeps the last data on screen.
 */
export function QueryView<T, E>({
  query,
  children,
  placeholder = <Skeleton className="h-48 w-full rounded-xl" />,
}: {
  query: UseQueryResult<T, E>;
  children: (data: T) => ReactNode;
  placeholder?: ReactNode;
}) {
  const { t } = useTranslation();
  const failure = query.error ?? query.failureReason;
  if (query.data !== undefined) {
    return (
      <div className={cn("transition-opacity", query.isRefetching && "opacity-90")}>
        {errorCode(failure) === "CORE_UNAVAILABLE" ? (
          <div className="mb-4">
            <CoreUnavailable />
          </div>
        ) : null}
        {children(query.data)}
      </div>
    );
  }
  if (errorCode(failure) === "CORE_UNAVAILABLE") return <CoreUnavailable />;
  if (query.isError) {
    return (
      <div
        role="alert"
        className="flex items-center gap-2 rounded-xl border border-missing/40 bg-missing-bg p-4 text-sm text-missing-fg"
      >
        <CircleAlertIcon className="size-4 shrink-0" aria-hidden />
        {errorMessage(query.error, t)}
      </div>
    );
  }
  return placeholder;
}
